import React, { useEffect, useRef, useState } from "react";

/**
 * Counts up from 0 to a target number when the element scrolls into view.
 * Accepts a raw string like "5,200+", "4.9", "120+", "16" — automatically
 * splits into { numeric-target, suffix, decimals } and formats output
 * with the same locale grouping (commas). Respects prefers-reduced-motion.
 */
function parseTarget(input) {
  const s = String(input ?? "").trim();
  // Match: optional decimal number, then anything else is treated as suffix.
  const m = s.match(/^([\d,]*\.?\d+)\s*(.*)$/);
  if (!m) return { target: 0, decimals: 0, suffix: s, grouped: false };
  const numeric = m[1].replace(/,/g, "");
  const decimals = numeric.includes(".") ? numeric.split(".")[1].length : 0;
  return {
    target: parseFloat(numeric),
    decimals,
    suffix: m[2] || "",
    grouped: m[1].includes(","),
  };
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

export default function AnimatedNumber({
  value,
  duration = 1600,
  className = "",
  "data-testid": testId,
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");
  const startedRef = useRef(false);
  const { target, decimals, suffix, grouped } = parseTarget(value);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Reset the "has-started" gate whenever the target value changes
    // (SiteContent loads async, so initial value may be 0/undefined then update).
    startedRef.current = false;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const format = (n) => {
      const rounded = decimals ? n.toFixed(decimals) : Math.round(n).toString();
      const [intPart, decPart] = rounded.split(".");
      const withGroups = grouped ? Number(intPart).toLocaleString("en-US") : intPart;
      return decPart ? `${withGroups}.${decPart}` : withGroups;
    };
    if (reducedMotion) {
      setDisplay(format(target));
      return;
    }
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    const runAnimation = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        setDisplay(format(target * easeOutCubic(t)));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    // Fast path: if already in the viewport at mount, start immediately.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      runAnimation();
      return () => cancelAnimationFrame(raf);
    }

    // Otherwise attach an observer that fires when the element enters view.
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          runAnimation();
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [target, decimals, grouped, duration]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {display}
      {suffix}
    </span>
  );
}
